import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { farmerService } from "../../services/farmerService"
import { Farmer } from "../../types"

const regionName = (r: Farmer["region"]) =>
  typeof r === "object" && r !== null ? (r as any).name : r

function InfoBlock({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <p className="text-neutral-600 text-[10px] uppercase tracking-widest mb-1">{label}</p>
      <p className="text-white text-sm font-medium">{value ?? "—"}</p>
    </div>
  )
}

export default function FarmerDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [farmer,  setFarmer]  = useState<Farmer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!id) return
    farmerService.getById(Number(id))
      .then(setFarmer)
      .catch(() => setError("Farmer not found or failed to load."))
      .finally(() => setLoading(false))
  }, [id])

  const handleDelete = async () => {
    if (!farmer || !window.confirm(`Delete "${farmer.name}"? This cannot be undone.`)) return
    setDeleting(true)
    try {
      await farmerService.delete(farmer.id!)
      navigate("/admin/farmers", { replace: true })
    } catch {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3 text-neutral-500">
        <svg className="w-5 h-5 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <span className="text-sm">Loading farmer...</span>
      </div>
    )
  }

  if (error || !farmer) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-neutral-500 text-sm">{error ?? "Farmer not found."}</p>
        <button
          onClick={() => navigate("/admin/farmers")}
          className="text-emerald-400 hover:text-emerald-300 text-xs border border-emerald-500/20 px-4 py-2 rounded-xl transition-colors"
        >
          ← Back to Farmers
        </button>
      </div>
    )
  }

  const totalTrees = farmer.trees?.reduce((s, t) => s + (Number(t.count) || 0), 0) ?? 0
  const hasLocation = farmer.latitude != null && farmer.longitude != null

  return (
    <div className="space-y-5 max-w-4xl">

      {/* Back + actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/admin/farmers")}
          className="flex items-center gap-2 text-neutral-500 hover:text-white text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Farmers
        </button>

        <button
          onClick={handleDelete}
          disabled={deleting}
          className="flex items-center gap-2 text-red-400 hover:text-red-300 text-xs border border-red-500/20 hover:border-red-500/40 px-4 py-2 rounded-xl transition-all disabled:opacity-40"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          {deleting ? "Deleting..." : "Delete Farmer"}
        </button>
      </div>

      {/* Hero card */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-emerald-400 text-xs bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-lg">
                {farmer.farmer_code ?? "N/A"}
              </span>
            </div>
            <h2 className="text-white text-2xl font-black tracking-tight mt-2">{farmer.name}</h2>
            <p className="text-neutral-500 text-sm mt-0.5">{farmer.phone}</p>
          </div>

          {/* Tree count badge */}
          <div className="text-right">
            <p className="text-emerald-400 text-4xl font-black">{totalTrees.toLocaleString()}</p>
            <p className="text-neutral-600 text-xs mt-0.5">total trees</p>
          </div>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Personal info */}
        <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5 space-y-4">
          <p className="text-white text-sm font-bold border-b border-white/[0.06] pb-3">Farmer Info</p>
          <InfoBlock label="Full Name" value={farmer.name} />
          <InfoBlock label="Phone" value={farmer.phone} />
          <InfoBlock label="Region" value={regionName(farmer.region)} />
          <InfoBlock
            label="Registered"
            value={farmer.created_at
              ? new Date(farmer.created_at).toLocaleDateString("en-GB", {
                  weekday: "long", day: "2-digit", month: "long", year: "numeric",
                })
              : undefined}
          />
        </div>

        {/* Farm info */}
        <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5 space-y-4">
          <p className="text-white text-sm font-bold border-b border-white/[0.06] pb-3">Farm Details</p>
          <InfoBlock label="Farm Size" value={farmer.farm_size_range} />
          <InfoBlock label="Arable Size" value={farmer.arable_size_range} />
          <InfoBlock label="Tree Coverage" value={farmer.tree_coverage_range} />
          {hasLocation && (
            <div>
              <p className="text-neutral-600 text-[10px] uppercase tracking-widest mb-1">GPS Location</p>
              <div className="flex items-center gap-2">
                <p className="text-white text-sm font-mono">
                  {Number(farmer.latitude).toFixed(6)}, {Number(farmer.longitude).toFixed(6)}
                </p>
                <a
                  href={`https://www.google.com/maps?q=${farmer.latitude},${farmer.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-emerald-400 hover:text-emerald-300 text-xs border border-emerald-500/20 px-2 py-0.5 rounded-lg transition-colors"
                >
                  View map ↗
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tree inventory */}
      {farmer.trees && farmer.trees.length > 0 && (
        <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/[0.06]">
            <p className="text-white text-sm font-bold">Tree Inventory</p>
            <p className="text-neutral-600 text-xs mt-0.5">{farmer.trees.length} species registered</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.04]">
                  {["Species", "Count", "Age (yrs)", "Condition"].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-[11px] font-semibold text-neutral-600 tracking-wider uppercase">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {farmer.trees.map((tree, i) => {
                  const conditionColor = {
                    good: "text-emerald-400 bg-emerald-500/10",
                    fair: "text-yellow-400 bg-yellow-500/10",
                    poor: "text-red-400 bg-red-500/10",
                  }[tree.condition] ?? "text-neutral-400 bg-white/5"

                  return (
                    <tr
                      key={i}
                      className={`border-b border-white/[0.03] ${i === farmer.trees.length - 1 ? "border-none" : ""}`}
                    >
                      <td className="px-5 py-3.5 text-white font-medium">{tree.tree_type_name ?? "—"}</td>
                      <td className="px-5 py-3.5">
                        <span className="text-emerald-400 font-bold">{Number(tree.count).toLocaleString()}</span>
                      </td>
                      <td className="px-5 py-3.5 text-neutral-400">{tree.age_years ?? "—"}</td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${conditionColor}`}>
                          {tree.condition}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Visual bar breakdown */}
          <div className="px-5 py-4 border-t border-white/[0.04] space-y-2">
            {farmer.trees.map((tree, i) => {
              const pct = totalTrees > 0 ? (Number(tree.count) / totalTrees) * 100 : 0
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-neutral-400 text-xs">{tree.tree_type_name}</span>
                    <span className="text-emerald-400 text-xs font-bold">{pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

    </div>
  )
}